/**
 * iter 43 Phase 2 — Drag-and-drop visual field mapper for affidavit templates.
 *
 * Renders the uploaded court-form PDF page-by-page, lets the user drag/resize
 * placeholder boxes on top of it, and edit each box's data binding (kind +
 * data_path) in a side panel. Boxes are stored in the same shape that
 * `services/proof_service.py` expects:
 *
 *   { id, page, x, y, width, height, font_size, kind, data_path }
 *
 * Coordinate system: react-pdf's page renders top-left, but reportlab/pypdf
 * use bottom-left. We translate on save (`y_pdf = pageHeight - y_screen -
 * height`) and on load (`y_screen = pageHeight - y_pdf - height`). Pages are
 * rendered at a fixed 1.0 scale so the screen units map 1:1 to PDF points.
 */
import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Rnd } from "react-rnd";
import { Trash, Plus, FloppyDisk } from "@phosphor-icons/react";

// Configure pdfjs worker (CDN — works offline once cached; the same approach
// will continue to function inside a Capacitor wrap because Capacitor allows
// http/https URLs from a configured host).
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const KINDS = ["text", "signature", "photo", "qr", "checkbox"];

const DATA_PATH_PRESETS = {
  text: [
    "attempt.recipient_name", "attempt.recipient_relationship",
    "attempt.service_method", "attempt.address_text",
    "attempt.attempted_at", "attempt.lat", "attempt.lng",
    "attempt.weather", "attempt.lighting", "attempt.demeanor",
    "attempt.mileage", "attempt.notes",
    "job.job_number", "job.received_at",
    "case.case_number", "case.plaintiff", "case.defendant",
    "customer.name", "customer.email",
    "server.full_name", "server.email", "server.notary_commission_number",
    "server.notary_commission_expiry", "server.notary_state",
  ],
  signature: ["attempt.signature_url"],
  photo: ["attempt.photo_url"],
  qr: ["public_attempt_url"],
  checkbox: ["attempt.outcome", "case.case_number"],
};

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

export default function PdfFieldMapper({ pdfUrl, fields, onChange }) {
  const [numPages, setNumPages] = useState(0);
  const [pageSize, setPageSize] = useState({ width: 612, height: 792 }); // letter default
  const [activePage, setActivePage] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const pageRef = useRef(null);

  // Convert PDF coords (bottom-left) ↔ screen coords (top-left). The PDF page
  // height comes from PDF.js after load (see onPageLoad).
  const toScreen = (f, pageH) => ({
    ...f,
    _screen_y: pageH - (f.y || 0) - (f.height || 14),
  });
  const toPdf = (f, pageH) => {
    const y = pageH - (f._screen_y ?? f.y ?? 0) - (f.height || 14);
    const out = { ...f, y };
    delete out._screen_y;
    return out;
  };

  // Boxes for the current page (with screen y).
  const pageFields = (fields || [])
    .filter(f => (f.page ?? 0) === activePage)
    .map(f => toScreen(f, pageSize.height));

  const onPageLoad = (page) => {
    // PDF.js sends viewport in PDF points at scale=1.
    const vp = page.getViewport({ scale: 1 });
    setPageSize({ width: vp.width, height: vp.height });
  };

  const updateField = (id, patch) => {
    const next = (fields || []).map(f => {
      if (f.id !== id) return f;
      if ("_screen_y" in patch || "x" in patch || "width" in patch || "height" in patch) {
        // Translate the screen-space patch back to PDF coords.
        const merged = { ...toScreen(f, pageSize.height), ...patch };
        return toPdf(merged, pageSize.height);
      }
      return { ...f, ...patch };
    });
    onChange(next);
  };

  const removeField = (id) => onChange((fields || []).filter(f => f.id !== id));

  const addBox = () => {
    const presets = DATA_PATH_PRESETS.text;
    const screen_y = 80;
    const newField = {
      id: uid(),
      page: activePage,
      x: 72,
      y: pageSize.height - screen_y - 14,
      width: 220,
      height: 14,
      font_size: 10,
      kind: "text",
      data_path: presets[0],
    };
    onChange([...(fields || []), newField]);
    setSelectedId(newField.id);
  };

  // Selected field (PDF coords) → for the side panel.
  const selected = (fields || []).find(f => f.id === selectedId);

  return (
    <div className="flex gap-3" style={{ minHeight: 540 }} data-testid="pdf-field-mapper">
      {/* ---- LEFT: PDF + draggable boxes ---- */}
      <div style={{ flex: 1 }}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-semibold uppercase">Page</span>
          {Array.from({ length: numPages || 1 }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`btn btn-sm ${activePage === i ? "btn-primary" : "btn-ghost"}`}
              onClick={() => { setActivePage(i); setSelectedId(null); }}
              data-testid={`pdf-mapper-page-${i}`}
            >{i + 1}</button>
          ))}
          <button type="button" className="btn btn-secondary btn-sm ml-auto" onClick={addBox} data-testid="pdf-mapper-add-box">
            <Plus size={12} /> ADD BOX
          </button>
        </div>

        <div
          ref={pageRef}
          style={{
            position: "relative",
            width: pageSize.width,
            border: "1px solid hsl(var(--border))",
            background: "#f5f5f5",
          }}
          data-testid="pdf-mapper-canvas"
        >
          <Document file={pdfUrl} onLoadSuccess={p => setNumPages(p.numPages)}>
            <Page
              pageNumber={activePage + 1}
              onLoadSuccess={onPageLoad}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              scale={1}
            />
          </Document>

          {pageFields.map(f => (
            <Rnd
              key={f.id}
              size={{ width: f.width, height: f.height }}
              position={{ x: f.x, y: f._screen_y }}
              bounds="parent"
              onDragStop={(e, d) => updateField(f.id, { x: d.x, _screen_y: d.y })}
              onResizeStop={(e, dir, ref, delta, pos) => updateField(f.id, {
                x: pos.x, _screen_y: pos.y,
                width: parseFloat(ref.style.width),
                height: parseFloat(ref.style.height),
              })}
              onClick={() => setSelectedId(f.id)}
              style={{
                border: `2px solid ${selectedId === f.id ? "#2563eb" : "#0ea5e9"}`,
                background: selectedId === f.id ? "rgba(37,99,235,0.18)" : "rgba(14,165,233,0.10)",
                boxShadow: selectedId === f.id ? "0 0 0 1px #fff inset" : undefined,
                cursor: "move",
              }}
              data-testid={`pdf-mapper-box-${f.id}`}
            >
              <div style={{
                fontSize: 9, color: "#0c4a6e", padding: "2px 4px",
                pointerEvents: "none", userSelect: "none",
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {f.kind === "text" ? `{{ ${f.data_path} }}` : `[${f.kind}]`}
              </div>
            </Rnd>
          ))}
        </div>
      </div>

      {/* ---- RIGHT: side panel for the selected field ---- */}
      <div style={{ width: 280 }} className="surface p-3 text-sm space-y-2">
        {!selected && (
          <div className="text-xs text-muted-foreground" data-testid="pdf-mapper-noselect">
            Click an existing box to edit it, or use <strong>+ ADD BOX</strong>.
          </div>
        )}
        {selected && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase">Box {selected.id.slice(0, 4)}</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeField(selected.id)} data-testid="pdf-mapper-remove">
                <Trash size={12} />
              </button>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase">Kind</label>
              <select
                className="input"
                value={selected.kind}
                onChange={e => updateField(selected.id, { kind: e.target.value, data_path: DATA_PATH_PRESETS[e.target.value]?.[0] || "" })}
                data-testid="pdf-mapper-kind"
              >
                {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase">Data path</label>
              <input
                list={`paths-${selected.id}`}
                className="input mono"
                value={selected.data_path || ""}
                onChange={e => updateField(selected.id, { data_path: e.target.value })}
                data-testid="pdf-mapper-data-path"
              />
              <datalist id={`paths-${selected.id}`}>
                {(DATA_PATH_PRESETS[selected.kind] || []).map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
            {selected.kind === "text" && (
              <div>
                <label className="text-xs font-semibold uppercase">Font size</label>
                <input
                  type="number"
                  className="input"
                  min={6}
                  max={36}
                  value={selected.font_size ?? 10}
                  onChange={e => updateField(selected.id, { font_size: parseFloat(e.target.value) })}
                  data-testid="pdf-mapper-font-size"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs uppercase">x</label>
                <input type="number" className="input" value={selected.x} onChange={e => updateField(selected.id, { x: parseFloat(e.target.value) })} />
              </div>
              <div>
                <label className="text-xs uppercase">y</label>
                <input type="number" className="input" value={selected.y} onChange={e => updateField(selected.id, { y: parseFloat(e.target.value) })} />
              </div>
              <div>
                <label className="text-xs uppercase">w</label>
                <input type="number" className="input" value={selected.width} onChange={e => updateField(selected.id, { width: parseFloat(e.target.value) })} />
              </div>
              <div>
                <label className="text-xs uppercase">h</label>
                <input type="number" className="input" value={selected.height} onChange={e => updateField(selected.id, { height: parseFloat(e.target.value) })} />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              x/y are in PDF points from the bottom-left of page {(selected.page ?? 0) + 1}. The visual mapper translates from screen coords automatically on save.
            </p>
          </>
        )}
        <div className="border-t border-border pt-2 mt-2">
          <div className="text-xs text-muted-foreground" data-testid="pdf-mapper-count">
            {(fields || []).length} field(s) total · {pageFields.length} on this page
          </div>
        </div>
      </div>
    </div>
  );
}
